const API_BASE_URL = '/api/v1';

// Create API client function
const apiClient = async (url, options = {}) => {
  const token = localStorage.getItem('token');

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  const response = await fetch(`${API_BASE_URL}/${url}`, config);

  if (response.status === 401 || response.status === 403) {
    // Token expired or invalid - logout user
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userPermissions');
    window.location.href = '/login';
    throw new Error('Session expired. Please login again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}`);
  }

  return response.json();
};

// Cache configuration
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes (reduced for category data)
const cache = new Map();

class CategoryService {
    constructor() {
        this.baseURL = 'categories';
    }

    // Cache management
    setCache(key, data) {
        cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    getCache(key) {
        const cached = cache.get(key);
        if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
            return cached.data;
        }
        cache.delete(key);
        return null;
    }

    clearCache() {
        cache.clear();
    }

    invalidateCache(pattern) {
        for (const key of cache.keys()) {
            if (key.includes(pattern)) {
                cache.delete(key);
            }
        }
    }

    // Force refresh - clear all caches and reload from server
    async forceRefresh() {
        this.clearCache();
        // Optionally reload fresh data
        return await this.getCategories();
    }

    // API methods
    async getCategories(options = {}, bypassCache = false) {
        const cacheKey = `categories_${JSON.stringify(options)}`;

        if (!bypassCache) {
            const cached = this.getCache(cacheKey);
            if (cached) {
                return cached;
            }
        }

        try {
            const params = new URLSearchParams();

            if (options.search) params.append('search', options.search);
            if (options.includeInactive) params.append('includeInactive', 'true');

            const response = await apiClient(`${this.baseURL}?${params}`, { method: 'GET' });

            if (!bypassCache) {
                this.setCache(cacheKey, response);
            }

            return response;
        } catch (error) {
            console.error('Error fetching categories:', error);
            throw error;
        }
    }

    async getActiveCategories() {
        const cacheKey = 'active_categories';
        const cached = this.getCache(cacheKey);

        if (cached) {
            return cached;
        }

        try {
            const response = await apiClient(`${this.baseURL}/active`, { method: 'GET' });
            this.setCache(cacheKey, response);
            return response;
        } catch (error) {
            console.error('Error fetching active categories:', error);
            throw error;
        }
    }

    async getCategoriesWithSubcategories() {
        const cacheKey = 'categories_with_subcategories';
        const cached = this.getCache(cacheKey);

        if (cached) {
            return cached;
        }

        try {
            const response = await apiClient(`${this.baseURL}/with-subcategories`, { method: 'GET' });
            this.setCache(cacheKey, response);
            return response;
        } catch (error) {
            console.error('Error fetching categories with subcategories:', error);
            throw error;
        }
    }

    async getCategoryById(id) {
        const cacheKey = `category_${id}`;
        const cached = this.getCache(cacheKey);

        if (cached) {
            return cached;
        }

        try {
            const response = await apiClient(`${this.baseURL}/${id}`, { method: 'GET' });
            this.setCache(cacheKey, response);
            return response;
        } catch (error) {
            console.error('Error fetching category:', error);
            throw error;
        }
    }

    async createCategory(categoryData) {
        try {
            const response = await apiClient(this.baseURL, {
                method: 'POST',
                body: JSON.stringify(categoryData)
            });

            // Clear ALL category caches immediately
            this.clearCache();

            // Dispatch event to notify other components
            window.dispatchEvent(new CustomEvent('categoriesChanged', {
                detail: {
                    action: 'create',
                    data: categoryData
                }
            }));

            return response;
        } catch (error) {
            console.error('Error creating category:', error);
            throw error;
        }
    }

    async updateCategory(id, categoryData) {
        try {
            const response = await apiClient(`${this.baseURL}/${id}`, {
                method: 'PUT',
                body: JSON.stringify(categoryData)
            });

            // Clear ALL category-related caches immediately
            this.clearCache();

            // Dispatch event to notify other components
            window.dispatchEvent(new CustomEvent('categoriesChanged', {
                detail: {
                    categoryId: id,
                    action: 'update',
                    data: categoryData
                }
            }));

            return response;
        } catch (error) {
            console.error('Error updating category:', error);
            throw error;
        }
    }

    async deleteCategory(id) {
        try {
            const response = await apiClient(`${this.baseURL}/${id}`, { method: 'DELETE' });

            // Clear ALL category caches immediately
            this.clearCache();

            // Dispatch event to notify other components
            window.dispatchEvent(new CustomEvent('categoriesChanged', {
                detail: {
                    categoryId: id,
                    action: 'delete'
                }
            }));

            return response;
        } catch (error) {
            console.error('Error deleting category:', error);
            throw error;
        }
    }

    async toggleCategoryStatus(id, options = {}) {
        try {
            const params = new URLSearchParams();
            if (options.deactivateChildren) {
                params.append('deactivateChildren', 'true');
            }

            // Use fetch directly to access response headers
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/${this.baseURL}/${id}/toggle-status?${params}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
            });

            if (response.status === 401 || response.status === 403) {
                localStorage.removeItem('token');
                localStorage.removeItem('userEmail');
                localStorage.removeItem('userPermissions');
                window.location.href = '/login';
                throw new Error('Session expired. Please login again.');
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${response.status}`);
            }

            const result = await response.json();

            // Check for instant update headers
            const categoryChanged = response.headers.get('X-Category-Changed');
            const changedCategoryId = response.headers.get('X-Category-Id');
            const newStatus = response.headers.get('X-Category-Status');

            if (categoryChanged === 'true') {
                console.log('Category change detected, clearing caches instantly');

                // Clear all category caches immediately
                this.clearCache();

                // Dispatch custom event for components to listen to
                window.dispatchEvent(new CustomEvent('categoriesChanged', {
                    detail: {
                        categoryId: changedCategoryId,
                        newStatus: newStatus === 'true',
                        action: 'toggle'
                    }
                }));
            } else {
                // Fallback: clear caches normally
                this.invalidateCache('categories');
                this.invalidateCache('active_categories');
                this.invalidateCache(`category_${id}`);
            }

            return result;
        } catch (error) {
            console.error('Error toggling category status:', error);
            throw error;
        }
    }

    async bulkUpdateCategories(updates) {
        try {
            const response = await apiClient(`${this.baseURL}/bulk-update`, {
                method: 'POST',
                body: JSON.stringify({ updates })
            });

            // Clear ALL category caches immediately
            this.clearCache();

            // Dispatch event to notify other components
            window.dispatchEvent(new CustomEvent('categoriesChanged', {
                detail: {
                    action: 'bulk_update',
                    updates: updates
                }
            }));

            return response;
        } catch (error) {
            console.error('Error bulk updating categories:', error);
            throw error;
        }
    }

    async getCategoryStats() {
        const cacheKey = 'category_stats';
        const cached = this.getCache(cacheKey);

        if (cached) {
            return cached;
        }

        try {
            const response = await apiClient(`${this.baseURL}/stats`, { method: 'GET' });
            this.setCache(cacheKey, response);
            return response;
        } catch (error) {
            console.error('Error fetching category stats:', error);
            throw error;
        }
    }

    // Utility methods
    buildCategoryTree(categories) {
        const categoryMap = {};
        const roots = [];

        // Create a map for quick lookup
        categories.forEach(cat => {
            cat.subcategories = [];
            categoryMap[cat.id] = cat;
        });

        // Build the tree
        categories.forEach(cat => {
            if (cat.parent_id) {
                if (categoryMap[cat.parent_id]) {
                    categoryMap[cat.parent_id].subcategories.push(cat);
                }
            } else {
                roots.push(cat);
            }
        });

        return roots;
    }

    flattenCategoryTree(categories, level = 0) {
        const flattened = [];

        categories.forEach(cat => {
            flattened.push({
                ...cat,
                level,
                hasChildren: cat.subcategories && cat.subcategories.length > 0
            });

            if (cat.subcategories && cat.subcategories.length > 0) {
                flattened.push(...this.flattenCategoryTree(cat.subcategories, level + 1));
            }
        });

        return flattened;
    }

    findCategoryById(categories, id) {
        for (const category of categories) {
            if (category.id === id) {
                return category;
            }

            if (category.subcategories && category.subcategories.length > 0) {
                const found = this.findCategoryById(category.subcategories, id);
                if (found) return found;
            }
        }

        return null;
    }

    getCategoryPath(categories, id) {
        const category = this.findCategoryById(categories, id);
        return category ? category.fullPath || [] : [];
    }

    // Form data helpers
    prepareCategoryForForm(category) {
        return {
            id: category.id,
            name: category.name,
            name_ar: category.name_ar || '',
            description: category.description || '',
            description_ar: category.description_ar || '',
            image_url: category.image_url || '',
            is_active: category.is_active,
            sort_order: category.sort_order,
            parent_id: category.parent_id || null
        };
    }

    validateCategoryData(data) {
        const errors = {};

        if (!data.name || data.name.trim().length === 0) {
            errors.name = 'Category name is required';
        } else if (data.name.length > 100) {
            errors.name = 'Category name cannot exceed 100 characters';
        }

        if (data.name_ar && data.name_ar.length > 100) {
            errors.name_ar = 'Arabic name cannot exceed 100 characters';
        }

        if (data.description && data.description.length > 500) {
            errors.description = 'Description cannot exceed 500 characters';
        }

        if (data.description_ar && data.description_ar.length > 500) {
            errors.description_ar = 'Arabic description cannot exceed 500 characters';
        }

        if (data.image_url && !this.isValidUrl(data.image_url)) {
            errors.image_url = 'Image URL must be a valid URL';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    isValidUrl(string) {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    }
}

export default new CategoryService();
